/**
 * The admin's own "Capture my logs" window (admin Settings -> Support): the backend's signed
 * token and when it ends. Kept in localStorage so a reload or a second tab keeps logging until the
 * 30 minutes are up. Every backend request sends the token as X-Support-Logging while it is
 * unexpired (see BaseBackendServiceHttp), which tags the request logs for the support portal.
 * The investor site never stores one, so it never sends the header.
 */

export const SUPPORT_LOGGING_HEADER = 'X-Support-Logging';
const STORAGE_KEY = 'fides.supportLogging';

export interface SupportLoggingSession {
  token: string;
  expiresAt: number;
}

export function readSupportLoggingSession(now: number = Date.now()): SupportLoggingSession | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as SupportLoggingSession;
    if (typeof session?.token !== 'string' || !(session.expiresAt > now)) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function saveSupportLoggingSession(session: SupportLoggingSession): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage blocked: logging still runs for this page, it just will not survive a reload.
  }
}

export function clearSupportLoggingSession(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored to clear.
  }
}

/** The header to add to a backend request, or none when logging is off or expired. */
export function supportLoggingHeaders(): Record<string, string> {
  const session = readSupportLoggingSession();
  return session ? { [SUPPORT_LOGGING_HEADER]: session.token } : {};
}
